import {
    RiCloseLine, RiEdit2Line, RiEye2Line, RiLockLine,
    RiShieldUserLine,
    RiUserForbidLine,
} from "@remixicon/react";
import React, { useState } from "react";
import { PermissionAction, PermissionAssignment } from "../../../../__generated__/api";
import AgentPicker from "../../../../components/pickers/agents/Agents";
import Popover from "../../../../components/popover/Popover";
import Button, { SizeTypes, VariantTypes } from "../../../../components/theme/button/Button";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import Icon from "../../../../components/theme/icon/Icon";
import Toggle from "../../../../components/theme/toggle/Toggle";
import Styles from "./ArticlePermissions.module.scss";
import { EVERYONE_GROUP_ID } from "../../../../components/pickers/user-groups/UserGroups";
import Text, { ColorVariant } from "../../../../components/theme/text/Text";

const GROUP_EVERYONE = "urn:fq:user-group:everyone";

function PickerLine({
    permissions,
    setPermissions,
    action,
    icon,
    toggleText,
    emptyText,
} :{
    permissions: PermissionAssignment[],
    setPermissions(permissions: PermissionAssignment[]): void;
    action: PermissionAction;
    icon: React.ReactNode;
    toggleText: string;
    emptyText: string;
}) {
    const readUsers = permissions
        .filter(row => row.action === action && row.assigneeId && row.assigneeId !== GROUP_EVERYONE)
        .toIdList("assigneeId");

    return (
        <div className={Styles["menu-item"]}>
            <AgentPicker
                onSelected={memberIds => {
                    const current = permissions.filter(
                        row => row.action !== action && row.assigneeId,
                    ).filter(row => row.assigneeId !== GROUP_EVERYONE);

                    if (memberIds?.isEmpty) {
                        setPermissions([
                            {
                                assigneeId: GROUP_EVERYONE,
                                action,
                            },
                        ]);
                    } else {
                        setPermissions(current.concat(memberIds?.map(assigneeId => ({
                            assigneeId,
                            action,
                        })) ?? []));
                    }
                }}
                value={readUsers}
                isMulti
                isClearable
                shouldIncludeGroups
                shouldShowDropdown
                className="flex-grow-1"
                toggleText={toggleText}
                togglePostfix={readUsers.isEmpty ? emptyText : ""}
                searchMinimum={5}
                icon={icon}
            />

            <Button
                prefixIcon={<RiCloseLine />}
                size="small"
                variant="none"
                onClick={() => {
                    setPermissions(permissions.filter(
                        row => row.action !== action && row.assigneeId,
                    ).concat([
                        {
                            assigneeId: GROUP_EVERYONE,
                            action,
                        },
                    ]));
                }}
                className={readUsers.isNotEmpty ? "visible" : "invisible"}
            />
        </div>
    );
}
export default function ArticlePermissions({
    value,
    onChange,
    title,
    text,
    toggleSize,
    toggleVariant,
    shouldColor,
}: {
    value: PermissionAssignment[],
    onChange(value: PermissionAssignment[]): void;
    title?: string;
    text?: string;
    toggleSize?: SizeTypes;
    toggleVariant?: VariantTypes;
    shouldColor?: boolean;
}) {
    const [shouldShow, setShouldShow] = useState(false);
    const [permissions, setPermissions] = useState<PermissionAssignment[]>(value);

    const isPublic = value?.filter(row => row.action === PermissionAction.Read
            && row.assigneeId !== EVERYONE_GROUP_ID).isEmpty ?? true;
    let textColorVariant: ColorVariant | undefined;
    if (shouldColor) {
        textColorVariant = isPublic ? "body-secondary" : "danger";
    }
    return (
        <Dropdown
            show={shouldShow}
            // onToggle={nextValue => setShouldShow(nextValue)}
            className={Styles["article-permissions"]}
            align="end"
            drop="up"
        >
            <Dropdown.Toggle
                size={toggleSize}
                variant={toggleVariant}
                onClick={() => setShouldShow(!shouldShow)}
                prefixIcon={(
                    <Icon
                        icon={<RiUserForbidLine />}
                        size="xsmall"
                        variant={!shouldColor || isPublic ? "default" : "danger"}
                    />
                )}
            >
                <Text
                    colorVariant={textColorVariant}
                    as="span"
                    textVariant="body-2-medium"
                >
                    {isPublic ? "Public" : "Private"}
                </Text>
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <div className={Styles.menu}>
                    <Text as="h2" textVariant="subtitle-2">{title}</Text>
                    <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                        {text}
                    </Text>

                    <PickerLine
                        permissions={permissions}
                        setPermissions={setPermissions}
                        action={PermissionAction.Read}
                        icon={<RiEye2Line />}
                        toggleText="View"
                        emptyText="Public"
                    />
                    <PickerLine
                        permissions={permissions}
                        setPermissions={setPermissions}
                        action={PermissionAction.Modify}
                        icon={<RiEdit2Line />}
                        toggleText="Modify"
                        emptyText="Agents"
                    />
                    <div className={Styles["menu-actions"]}>

                        <Button
                            variant="primary"
                            size="small"
                            onClick={() => {
                                setShouldShow(false);
                                onChange(permissions);
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            variant="outline-gray"
                            size="small"
                            onClick={() => {
                                setShouldShow(false);
                                setPermissions(value);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Dropdown.Menu>

        </Dropdown>
    );
}

ArticlePermissions.defaultProps = {
    title: "Section Permissions",
    text: "Section permissions allow you to control who can view or modify the content of this section.",
    toggleSize: "xsmall",
    toggleVariant: "ghost-gray",
    shouldColor: true,
};
